/**
 * RealOverbookingDashboard (P2 rewrite — Feb 2026)
 * ──────────────────────────────────────────────────────────────────────
 * Production-safe rewrite: depends ONLY on endpoints we ship today:
 *
 *   GET /api/scheduler/overbooking/policy   ← provider/tenant policy +
 *                                              contingency feature flag
 *   GET /api/scheduler/slots/capacity       ← per-provider / per-day
 *                                              capacity utilisation
 *   GET /api/scheduler/overbooking/audit    ← historical overbooking
 *                                              actions log (book/release)
 *
 * The earlier dependency on `/overbooking/analytics` and
 * `/overbooking/collisions` is gone — those endpoints aren't available
 * in real mode, and gating Insights on them led to a permanent
 * "api_missing" panel even when the underlying policy/capacity/audit
 * data WAS available.
 *
 * Contingency overbooking (POST /api/scheduler/overbooking/contingencies)
 * is feature-gated. We probe its availability via the policy response:
 * if policy.contingencies_enabled is false OR a HEAD-style probe
 * returns 404/501, contingency CTAs hide and a non-blocking note
 * appears in the policy KPI card. Existing actions never raise an
 * error toast in degraded mode.
 *
 * Reports `insights` source via the SchedulerTraceContext rollup:
 *   loading | live | error | api_missing | empty
 *
 * NO PHI in any of the 3 endpoint responses — they carry counts,
 * percents, policy thresholds, and audit timestamps only.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Activity, AlertTriangle, RefreshCw, Loader2, Sparkles, ShieldOff,
  TrendingUp, Clock, Settings as SettingsIcon,
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { toast } from 'sonner';
import * as schedulerApi from '../../../../lib/api/schedulerApi';
import { useReportSchedulerSource } from '../../../../lib/scheduler/SchedulerTraceContext';

const KpiCard = ({ icon: Icon, label, value, sub, accent = 'slate', testId }) => (
  <div className={`rounded-xl border border-${accent}-200 bg-white p-3`} data-testid={testId}>
    <div className="flex items-center gap-1.5">
      {Icon && <Icon className={`h-3 w-3 text-${accent}-500`} />}
      <p className={`text-[9px] uppercase font-semibold text-${accent}-500 tracking-wide`}>{label}</p>
    </div>
    <p className="text-[18px] font-bold text-slate-900 tabular-nums mt-1" data-testid={`${testId}-value`}>{value}</p>
    {sub && <p className="text-[10px] text-slate-500 mt-0.5" data-testid={`${testId}-sub`}>{sub}</p>}
  </div>
);

const fmtPct = (v) => (v == null ? '—' : `${Math.round(v * (v > 1 ? 1 : 100))}%`);
const fmtNum = (v) => (v == null ? '—' : (typeof v === 'number' ? v.toLocaleString() : String(v)));

const RealOverbookingDashboard = ({ allowApply = true }) => {
  const [policy, setPolicy]     = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [audit, setAudit]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  // Each endpoint tracks its own missing flag so a partial outage
  // doesn't collapse the whole panel.
  const [missing, setMissing] = useState({ policy: false, capacity: false, audit: false });
  // Contingency feature flag — true when policy reports it on AND no
  // explicit 404/501 came back from a contingencies probe.
  const [contingencyAvailable, setContingencyAvailable] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    const [pol, cap, aud] = await Promise.all([
      schedulerApi.getOverbookingPolicy(),
      schedulerApi.getSlotCapacity({ window: 'this_week' }),
      schedulerApi.getOverbookingAudit({ window: 'this_week' }),
    ]);
    const nextMissing = { policy: false, capacity: false, audit: false };

    if (pol?.ok)             { setPolicy(pol.data || {}); }
    else if (pol?.error?.status === 404 || pol?.error?.status === 501) { setPolicy(null); nextMissing.policy = true; }
    else                     { setPolicy(null); setError(pol?.error || null); }

    if (cap?.ok)             { setCapacity(cap.data || {}); }
    else if (cap?.error?.status === 404 || cap?.error?.status === 501) { setCapacity(null); nextMissing.capacity = true; }
    else                     { setCapacity(null); setError((e) => e || cap?.error || null); }

    if (aud?.ok)             { setAudit(aud.data || {}); }
    else if (aud?.error?.status === 404 || aud?.error?.status === 501) { setAudit(null); nextMissing.audit = true; }
    else                     { setAudit(null); setError((e) => e || aud?.error || null); }

    setMissing(nextMissing);

    // Contingency probe — only fire when policy already says it's on.
    // If the policy itself doesn't expose the flag, default to false
    // and keep contingency CTAs hidden. Safer than assuming-on.
    const policyEnables = !!(pol?.ok && (pol.data?.contingencies_enabled ?? pol.data?.contingencies?.enabled));
    if (policyEnables) {
      const probe = await schedulerApi.listContingencies({ limit: 1 });
      if (probe?.ok) setContingencyAvailable(true);
      else if (probe?.error?.status === 404 || probe?.error?.status === 501) setContingencyAvailable(false);
      else setContingencyAvailable(false);
    } else {
      setContingencyAvailable(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /* ─── Trace state ─────────────────────────────────────────── */
  const allMissing = missing.policy && missing.capacity && missing.audit;
  const insightsSource =
    loading                                ? 'loading'
    : error                                ? 'error'
    : allMissing                           ? 'api_missing'
    : (policy || capacity || audit)        ? 'live'
    : 'empty';
  useReportSchedulerSource('insights', insightsSource);

  /* ─── Derive KPI values ───────────────────────────────────── */
  const capacityUsedPct = capacity?.utilization?.weekly
    ?? capacity?.utilization_pct
    ?? capacity?.usage_pct
    ?? null;
  const overbookCapPct = policy?.max_overbook_pct
    ?? policy?.max_overbook_percent
    ?? policy?.threshold_pct
    ?? null;
  const headroomPct = (capacityUsedPct != null) ? Math.max(0, 1 - capacityUsedPct) : null;

  const auditItems = audit?.items || audit?.events || audit?.audit || [];
  const overbooksThisWeek = auditItems.filter((a) => (a.action || a.type) === 'book').length;
  const releasesThisWeek  = auditItems.filter((a) => (a.action || a.type) === 'release').length;
  const lastAuditAt = auditItems.length > 0
    ? (auditItems[0].at || auditItems[0].timestamp || auditItems[0].created_at || null)
    : null;

  /* ─── Render: api-missing FALLBACK ────────────────────────── */
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[11px] text-slate-500 inline-flex items-center gap-1.5"
           data-testid="real-overbooking-loading" data-insights-source="loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading overbooking insights…
      </div>
    );
  }

  if (allMissing) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3"
           data-testid="real-overbooking-api-missing" data-insights-source="api_missing">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-amber-900">Overbooking insights not yet provisioned</p>
          <p className="text-[10px] text-amber-700 mt-0.5">
            Insights tab depends on three production endpoints. None are reachable yet:
          </p>
          <ul className="text-[10px] text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
            <li><code>GET /api/scheduler/overbooking/policy</code></li>
            <li><code>GET /api/scheduler/slots/capacity</code></li>
            <li><code>GET /api/scheduler/overbooking/audit</code></li>
          </ul>
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 mt-2" onClick={reload} data-testid="real-overbooking-retry">
            <RefreshCw className="h-3 w-3" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (error && !policy && !capacity && !audit) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4 flex items-center justify-between gap-3"
           data-testid="real-overbooking-error" data-insights-source="error">
        <p className="text-[11px] text-rose-700">Failed to load overbooking insights: {error.message || 'unknown'}</p>
        <Button variant="outline" size="sm" onClick={reload} data-testid="real-overbooking-retry">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  /* ─── Render: KPI cards (live) ────────────────────────────── */
  return (
    <div data-testid="real-overbooking-wrapper" data-insights-source="live">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-[11px] font-semibold text-slate-800">Overbooking insights</p>
          <Badge className="text-[9px] bg-slate-100 text-slate-700 border border-slate-200">
            policy · capacity · audit
          </Badge>
        </div>
        <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1 text-slate-500" onClick={reload} data-testid="real-overbooking-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="real-overbooking-kpi-grid">
        <KpiCard
          icon={Activity}
          label="Capacity used"
          value={fmtPct(capacityUsedPct)}
          sub={capacity?.window || 'This week'}
          accent="emerald"
          testId="kpi-capacity-used"
        />
        <KpiCard
          icon={TrendingUp}
          label="Headroom"
          value={fmtPct(headroomPct)}
          sub={headroomPct != null && headroomPct < 0.1 ? 'Low — overbook risk' : 'Available capacity'}
          accent={headroomPct != null && headroomPct < 0.1 ? 'amber' : 'sky'}
          testId="kpi-headroom"
        />
        <KpiCard
          icon={SettingsIcon}
          label="Overbook cap"
          value={fmtPct(overbookCapPct)}
          sub={policy?.scope || 'tenant policy'}
          accent="violet"
          testId="kpi-policy-cap"
        />
        <KpiCard
          icon={Clock}
          label="Audit · this week"
          value={`${fmtNum(overbooksThisWeek)} / ${fmtNum(releasesThisWeek)}`}
          sub={`booked / released${lastAuditAt ? ` · last ${String(lastAuditAt).slice(11, 16)}` : ''}`}
          accent="slate"
          testId="kpi-audit"
        />
      </div>

      {/* Contingency feature-gate banner */}
      <div className="mt-3 rounded-xl border p-3 flex items-start gap-2"
           data-contingency-available={contingencyAvailable ? 'true' : 'false'}
           data-testid="real-overbooking-contingency-banner"
           style={{
             background:    contingencyAvailable ? 'rgba(16,185,129,0.04)' : 'rgba(100,116,139,0.04)',
             borderColor:   contingencyAvailable ? 'rgba(16,185,129,0.25)' : 'rgba(148,163,184,0.25)',
           }}>
        {contingencyAvailable ? (
          <>
            <Sparkles className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-slate-800">Contingency overbooking is on</p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                Echo will propose contingency slots when capacity exceeds {fmtPct(overbookCapPct)}.
              </p>
            </div>
            {allowApply && (
              <Button
                size="sm"
                className="h-7 text-[10px] bg-[#111] hover:bg-[#222] gap-1"
                onClick={async () => {
                  const res = await schedulerApi.listContingencies({ limit: 50 });
                  if (res?.ok) toast.info(`Open contingencies: ${(res.data?.items || res.data?.contingencies || []).length}`);
                  else toast.info('No contingencies right now.');
                }}
                data-testid="real-overbooking-list-contingencies"
              >
                Review contingencies
              </Button>
            )}
          </>
        ) : (
          <>
            <ShieldOff className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-slate-700">Contingency overbooking is unavailable</p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {policy?.contingencies_enabled === false
                  ? 'Disabled by tenant policy.'
                  : 'The contingency endpoints (POST /api/scheduler/overbooking/contingencies, /offer, /fill, /release) are not provisioned for this tenant. Capacity insights remain available above.'}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Per-endpoint partial-outage note */}
      {(missing.policy || missing.capacity || missing.audit) && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/40 p-2.5 text-[10px] text-slate-500"
             data-testid="real-overbooking-partial-outage">
          Partial degradation:&nbsp;
          {missing.policy   && <code className="mr-1">policy</code>}
          {missing.capacity && <code className="mr-1">slots/capacity</code>}
          {missing.audit    && <code className="mr-1">audit</code>}
          not provisioned. KPIs above are populated from the remaining live endpoints only.
        </div>
      )}
    </div>
  );
};

export default RealOverbookingDashboard;
